/*
 * The visit's order calls, shared (hubfix-live-orders, owner 10-23). CareIQ's Add (CareIQTabN3 handleAdd) has
 * always built its POST orders/create body here-in-place; the live Orders card in the LiveScribe panel and the
 * end-of-visit review (when the CareIQ tab is not open) place through the SAME endpoint with the SAME body, so
 * there is one placement path, not a new one:
 *   POST   /v1/{practice}/patient/{patient}/orders/create       orderCreateBody(...)  -> order id (HTTP 200 + int)
 *   DELETE /v1/{practice}/orders/delete/multiple                the patient-scoped delete CareIQ's Undo uses
 *   POST   /v1/{practice}/orders/get                            the visit's orders (the chart's order date)
 *   PUT    /v1/{practice}/tasks/update_status_schedule         Today <-> Schedule of a placed order (the dashboard
 *                                                               card's existing call: {orderid, statusflag, login_user})
 * Pure body builders + thin wrappers; nothing here decides what to order.
 */
import dayjs from "dayjs";
import careiqapi from "../../../../../api/careiqapi";
import { normalizeApptId } from "../../../../../utils/recordingCompletion";
import { clinicTodayKey } from "../../../../cards/visitflow";

// The chart's order date of a visit row (CareIQTabN3 chartOrderDate).
export const orderDateOf = (visit) => {
  const rawDate = visit ? visit.startdate || visit.encounterdate || visit.date : null;
  const parsed = rawDate ? dayjs(rawDate) : null;
  return parsed && parsed.isValid() ? parsed.format("YYYY-MM-DD") : clinicTodayKey();
};

// What a visit row contributes to every order body (CareIQTabN3: patient name, order date, appointment,
// encounter, provider). me = the login's username.
export const visitOrderContext = (visit, me) => ({
  patientName: `${(visit && visit.firstname) || ""} ${(visit && visit.lastname) || ""}`.trim(),
  me: me || "",
  orderDate: orderDateOf(visit),
  appointmentId: visit ? visit.appointmentid : null,
  encounterId: Number(visit && visit.encounterid) || 0,
  provider: visit ? visit.linked_provider || visit.providername || "" : "",
});

// The orders/create body (exactly CareIQTabN3's createBody, which now calls this). opts.when "today" | "schedule"
// (owner 10-24, OrderReview/orderWhen.js) sets the existing schedule flag ("f" / "t"); without it the body is
// unchanged (the gateway's default "f").
export const orderCreateBody = (ctx, ordername, ordertype, diagnosis = [], opts = {}) => ({
  patient_name: ctx.patientName,
  createdby: ctx.me,
  orderdate: ctx.orderDate,
  ordername,
  ordertype,
  tov: "",
  lov: 0,
  // a rec's order with a documented covering diagnosis: the chosen ICD-10 code (orders_master.diagnosis)
  diagnosis,
  appointmentid: normalizeApptId(ctx.appointmentId) ? Number(normalizeApptId(ctx.appointmentId)) : ctx.fallbackAppointmentId,
  encounterid: ctx.encounterId || 0,
  provider: ctx.provider,
  token_no: 0,
  confirm_status: "C",
  update_status: "C",
  ...(opts && (opts.when === "schedule" || opts.when === "today") ? { schedule: opts.when === "schedule" ? "t" : "f" } : {}),
});

// POST orders/create -> the new order id, or null. (A failure can come back as HTTP 201 {"status":201,...}.)
export const createVisitOrder = async (practiceId, patientId, body) => {
  let res = null;
  try {
    res = await careiqapi.post(`/v1/${practiceId}/patient/${patientId}/orders/create`, body);
  } catch (_) {
    res = null;
  }
  return res && res.status === 200 && Number.isInteger(res.data) ? res.data : null;
};

// The patient-scoped delete -> true when gone (200, or 404 = not this patient's / already gone).
export const deleteVisitOrder = async (practiceId, { orderId, orderDate, patientId, ordertype }) => {
  let status = 0;
  try {
    const r = await careiqapi.delete(`/v1/${practiceId}/orders/delete/multiple`, {
      data: { ordersArray: [orderId], orderdate: orderDate, patientid: Number(patientId), ordertype },
    });
    status = r && Number(r.status) ? Number(r.status) : 200;
  } catch (e) {
    status = (e && e.response && e.response.status) || 0;
  }
  return status === 200 || status === 404;
};

// The visit's orders (CareIQTabN3 fetchOrders' read) -> array, or null on failure.
// opts.timeoutMs (hubfix-oi-1026): an axios timeout, so a hung read ends (-> null) instead of waiting forever
export const fetchVisitOrders = async (practiceId, patientId, orderDate, opts = {}) => {
  try {
    const body = { patientid: patientId, orderdate: orderDate };
    const raw = opts && opts.timeoutMs
      ? await careiqapi.post(`/v1/${practiceId}/orders/get`, body, { timeout: opts.timeoutMs })
      : await careiqapi.post(`/v1/${practiceId}/orders/get`, body);
    const d = raw && raw.data !== undefined ? raw.data : raw;
    return Array.isArray(d) ? d : (d && (d.orders || d.data)) || [];
  } catch (_) {
    return null;
  }
};

/*
 * Today / Schedule of an order already placed (owner 10-24): the dashboard card's EXISTING call
 * (brainycarehome handleconfirmScheduleClick: PUT tasks/update_status_schedule {orderid, statusflag "t"|"f",
 * login_user}). That endpoint takes an order id only, so the change is scoped HERE: the order must be on THIS
 * patient's visit (fetchOrders = the visit's orders/get by patient + visit date) before it is changed, and the
 * result is read back. -> { ok, error } (error: a short line for the row). Never throws.
 */
// Review 10-24 (H1): orders/get reads orders_master_history for a PAST order date while update_status_schedule
// writes orders_master by id, and ids collide across the two tables (other patients' orders). So a placed order is
// switchable ONLY on today's visit (clinic date), with its id read from today's orders/get and that row's patientid
// equal to the chart's patient.
export const placedWhenEditable = (orderDate) => Boolean(orderDate) && orderDate === clinicTodayKey();

export const changeVisitOrderWhen = async ({ practiceId, me, patientId, orderDate, fetchOrders }, orderId, when) => {
  const flag = when === "schedule" ? "t" : "f";
  if (!placedWhenEditable(orderDate)) return { ok: false, error: "Not changed · only today's visit can be changed" };
  if (patientId == null || String(patientId) === "") return { ok: false, error: "Not changed · no patient" };
  const mine = (o) => o && o.id != null && String(o.id) === String(orderId) && o.patientid != null
    && String(o.patientid) === String(patientId);
  let list = null;
  try { list = await fetchOrders(); } catch (_) { list = null; }
  if (!Array.isArray(list)) return { ok: false, error: "Not changed · could not read this visit's orders" };
  if (!list.some(mine)) {
    return { ok: false, error: "Not changed · order not on this visit" };
  }
  let res = null;
  try {
    res = await careiqapi.put(`/v1/${practiceId}/tasks/update_status_schedule`, {
      orderid: Number(orderId), statusflag: flag, login_user: me || "",
    });
  } catch (_) {
    res = null;
  }
  // careiqapi resolves to the body: {status: 200, ...}; "not found" comes back as {status: 201}
  if (!(res && Number(res.status) === 200)) return { ok: false, error: "Not changed · try again" };
  try {
    const after = await fetchOrders();
    const o = Array.isArray(after) ? after.find(mine) : null;
    if (o && o.schedule != null && String(o.schedule) !== "" && String(o.schedule) !== flag) {
      return { ok: false, error: "Not changed · the order still shows the old setting" };
    }
  } catch (_) { /* the update returned 200: shown as changed */ }
  return { ok: true, error: null };
};
